import React, { useContext, useState } from "react";
import moment from "moment";
import Slide from "@mui/material/Slide";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
// SCSS
import styles from "./index.module.scss";
// Component
import Select from "../../FormControl/Select/Select";
import DatePickerWithInput from "Components/NewOrder/DatePicker/DatePickerWithInput";
import { getDeliveryTimes, transferOrderBurqToOnfleet } from "api/order";
import { AppContext } from "Context/AppContext";
import ConfirmationForTransferToOnfleet from "./ConfirmationForTransferToOnfleet";
import { useNavigate } from "react-router-dom";
import { pstToUTCDateTime, timeStamptoPSTime, utcDateTimeToTime } from "Pages/Order/Order/utility/util";

/**
 * Transition component for Material-UI Dialog slide animation
 * Slides the modal up from the bottom when opening
 */
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * TransferToOnFleetModal Component
 * 
 * Modal component for transferring an order from Burq delivery to OnFleet delivery.
 * Allows users to select a new delivery date and time slot from available OnFleet options.
 * 
 * @param {boolean} showModal - Controls the visibility of the modal dialog
 * @param {string} pageTitle - Title to display in the modal header
 * @param {function} handleCancelClick - Callback function when user cancels/closes the modal
 * @param {function} close - Function to close the modal
 * @param {object} orderDetails - Order object containing order information, recipients, and store details
 */
const TransferToOnFleetModal = ({ showModal, pageTitle, handleCancelClick, close, orderDetails }) => {
  // Context values for toast messages, navigation, and loading state
  const { showToastMessage, lastOrderEditPath, setIsLoading } = useContext(AppContext);
  
  // State for managing delivery time dropdown loading state
  const [timeSelectionDropdown, setTimeSelectionDropdown] = useState(false);
  
  // Array of available OnFleet delivery time options
  const [options, setOptions] = useState([]);
  
  // Selected delivery time option ID
  const [selectTime, setSelectTime] = useState(null)
  
  // CSS display value for time selection error message ("none" or "block")
  const [isTimeSelect, setTimeSelected] = useState("none")
  
  // CSS display value for date selection error message ("none" or "block")
  const [isDateSelected, setDateSelected] = useState("none")
  
  // Complete selected OnFleet option object containing delivery details
  const [selectedOnFleetOption, setSelectedOnFleetOption] = useState(null)
  
  // Controls visibility of the confirmation modal
  const [open, setOpen] = useState(false)
  const navigate = useNavigate();

  // Selected delivery date in MM/DD/YYYY format
  // Selected delivery date in MM/DD/YYYY format
  const [deliveryDate, setDeliveryDate] = useState(null)
  
  /**
   * Handles delivery date selection
   * When a date is selected, fetches available delivery time slots for that date
   * 
   * @param {Date|string} day - Selected delivery date
   */
  const handleDeliveryDateChange = async (day) => {
    try {
      // Show loading state in time dropdown while fetching options
      if (day) { setTimeSelectionDropdown(true) }
      // Clear previous options
      setOptions([]);
      
      // Prepare request body with order recipient and address details
      let bodySend = {
        address_type_id: orderDetails.order_recipients[0].address_type_id,
        delivery_date: moment(day).format("MM/DD/YYYY"),
        full_address: orderDetails.order_recipients[0].recipient_address,
        order_by_time: moment().unix(),
        zip_code: orderDetails.order_recipients[0].zipcode,
        address_2: orderDetails.order_recipients[0].address_line_2 ?? "",
        attn: orderDetails.order_recipients[0].attn ?? "",
        orderTimezone: orderDetails.store.timezone
      }
      
      // Update delivery date state
      setDeliveryDate(bodySend.delivery_date)
      // Reset validation error states
      setTimeSelected("none")
      setDateSelected("none")
      
      // Fetch available delivery time slots for the selected date
      const deliveryTimes = await getDeliveryTimes(bodySend)
      if (deliveryTimes && deliveryTimes.status === 200) {
        // Hide loading state
        setTimeSelectionDropdown(false)
        // Reset selected time
        setSelectTime(null)
        // Process and set OnFleet delivery options
        setOnFleetOptions(deliveryTimes.data.data, day, bodySend.delivery_date)
      } else {
        showToastMessage(undefined, "Delivery slot are currently not available", "error", false);
      }
    } catch (error) {
      showToastMessage(undefined, error.data.message, "error", false);
    }
  }

  /**
   * Processes and formats OnFleet delivery options from API response
   * Converts time formats (PST to UTC) and creates option objects for the dropdown
   * 
   * @param {object} data - API response containing costList array with delivery options
   * @param {Date|string} day - Selected delivery date
   * @param {string} delivery_date - Delivery date in MM/DD/YYYY format
   * @returns {boolean} - Returns true on successful processing
   */
  const setOnFleetOptions = (data, day, delivery_date)=>{
    let options = [];
    // Map each delivery option from the costList to a formatted option object
    const onFleetOptions = data?.costList?.map((mat, idx) => {
      let delivery_time_type = mat.delivery_time_type;
      let id = mat.id ?? 0;
      
      // Convert delivery time to PST timezone for display
      let pst_time = timeStamptoPSTime(mat.delivery_time, orderDetails.store.timezone);
      
      // Convert PST time to UTC datetime for API submission
      let delivery_date_time = pstToUTCDateTime(day, mat.delivery_time, null, orderDetails.store.timezone);
      
      // Convert UTC datetime to time string for display
      let utc_time = utcDateTimeToTime(delivery_date_time);
      
      // Format display name with time type, time, and price
      let name = `${delivery_time_type} ${pst_time} for $${mat.price}`;
      let delivery_time = `${delivery_time_type} ${utc_time}`;
      
      // Special formatting for "Anytime" delivery type (no specific time)
      if (delivery_time_type === "Anytime") {
        name = `${delivery_time_type} for $${mat.price}`;
      }
      
      // Create option object with all necessary delivery information
      let onFleetOption = {
        id: idx+1,
        name: name,
        delivery_time_type: delivery_time_type,
        data: mat,
        delivery_date_time: delivery_date_time,
        delivery_date: moment(delivery_date, "MM/DD/YYYY").format("YYYY-MM-DD"),
        delivered_time: delivery_time,
        delivery_time_id: mat.id || "",
      }
      options.push(onFleetOption)
      return onFleetOption;
    })
    // Append new options to existing options array
    setOptions((prev)=>[...prev, ...options])
    return true
  }

  /**
   * Handles the transfer of order from Burq to OnFleet delivery
   * Called when user confirms the transfer in the confirmation modal
   * 
   * @param {Event} event - Click event from confirmation button
   */
  const handleChangeToOnFleet = async (event) => {
    try {
      setIsLoading(true)
      
      // Prepare request body with order details and selected OnFleet delivery option
      let sendBody = {
        order_id: orderDetails.id,
        order_type: orderDetails.order_type || 0,
        delivery_type_id: orderDetails.delivery_type_id || "",
        is_local_order: orderDetails.is_local_order || 1,
        is_subscribe_order: orderDetails.is_subscribe_order || 0,
        is_burq_order: 0, // Mark order as no longer a Burq order
        delivery_date: selectedOnFleetOption.delivery_date,
        delivery_date_time: selectedOnFleetOption.delivery_date_time,
        delivered_time_id: selectedOnFleetOption.delivery_time_id || "",
        delivered_time: selectedOnFleetOption.delivered_time,
        delivery_time_type: selectedOnFleetOption.delivery_time_type || "",
      }

      // API call to transfer order from Burq to OnFleet
      const response = await transferOrderBurqToOnfleet(sendBody);
      if (response && response.status === 200) {
        // Success: Show success message, close modals, and navigate back to order page
        showToastMessage(undefined, response.data.message || "Order type updated successfully", "success", false, 9000)
        close(false)
        setOpen(false)
        setIsLoading(false);
        navigate(lastOrderEditPath);
      } else {
        // Error response: Show error message and close modals
        showToastMessage(undefined, response.data.message || "Something went wrong", "error", false);
        setIsLoading(false);
        setOpen(false)
        close(false)
    }
    } catch (error) {
      // Exception handling: Show error message and close modals
      showToastMessage(undefined, error?.response?.data?.message || "Something went wrong", "error", false);
      setIsLoading(false);
      setOpen(false)
      close(false)
    }
  }

  /**
   * Handles selection of a delivery time option from the dropdown
   * Updates the selected option state and clears validation errors
   * 
   * @param {number} selectedOptionId - ID of the selected delivery time option
   */
  const handleDeliveryMatrixOptionSelection = (selectedOptionId) => {
    setSelectedOnFleetOption(null)
    // Find the complete option object from the options array
    const selectedOption = options.find((option) => option.id === selectedOptionId);
    setSelectedOnFleetOption(selectedOption)
    setSelectTime(selectedOptionId)
    // Clear validation error messages
    setTimeSelected("none")
    setDateSelected("none")
  }

  /**
   * Validates form inputs before opening confirmation modal
   * Shows appropriate error messages if date or time is not selected
   * Opens confirmation modal if both date and time are selected
   * 
   * @param {Event} event - Click event from Save button
   */
  const handleValidation = (event) => {
    // If date is selected, clear date error but check for time
    if (deliveryDate !== null) {
      setDateSelected("none")
      setTimeSelected("block")
    } else {
      // If no date selected, show both errors
      setTimeSelected("block")
      setDateSelected("block")
    }
    
    // If time is selected but no date, show only date error
    if (selectTime !== null && deliveryDate === null) {
      setTimeSelected("none")
      setDateSelected("block")
    } else if(selectTime !== null && deliveryDate !== null){
      // Both date and time are selected: clear errors and open confirmation modal
      setTimeSelected("none")
      setDateSelected("none")
      setOpen(true)
    }
  }
  return (
    <React.Fragment>
      {/* Confirmation modal - shown when user has selected both date and time */}
      {selectTime !== null ? <ConfirmationForTransferToOnfleet
        open={open}
        setOpen={setOpen}
        changeToOnFleet={handleChangeToOnFleet}
      /> : ""}
      
      {/* Main modal dialog for selecting delivery date and time */}
      <Dialog
        open={showModal}
        TransitionComponent={Transition}
        onClose={handleCancelClick}
        classes={{
          paper: styles.modalDialog,
        }}
        aria-labelledby="modal-slide-title"
        aria-describedby="modal-slide-description"
        PaperProps={{
          style: {
            minHeight: "28vh",
            minWidth: "45vw",
            overflow: "visible",
          },
        }}>
        <DialogContent className={`${styles.modalContent} `}>
          <div className={`text-center ${styles.modalBody}`}>
            {/* Modal title */}
            <h5 className={`text-center ${styles.modalTitle}`}>
              {pageTitle}
            </h5>
            
            {/* Form fields row */}
            <div className={`row px-1 py-1 text-center`}>
              {/* Delivery Date Picker Column */}
              <div className="col-md-6" style={{ textAlign: "left", position: "relative" }}>
                <DatePickerWithInput
                  label="Delivery Date"
                  blockDates={[]}
                  value={deliveryDate ? deliveryDate : null}
                  onChange={handleDeliveryDateChange}
                  datePickerclass={styles.datePicker}
                  showDateclass={true}
                  orderTimezone={orderDetails.store.timezone}
                />
                {/* Date validation error message */}
                <span className="mt-3" style={{ color: "red", display: isDateSelected, position: "absolute" }}>Please select delivery date</span>
              </div>
              
              {/* Delivery Time Selector Column */}
              <div className="col-md-6 mt-2" style={{ textAlign: "left", position: "relative" }}>
                {/* Conditional rendering based on options availability and loading state */}
                {options || timeSelectionDropdown ?
                  (
                    // Show loading state while fetching delivery times
                    timeSelectionDropdown ?
                      <Select
                        label="Calculating..."
                        value={""}
                        onChange={""}
                        options={[]}
                      /> :
                      // Show delivery time options dropdown
                      <Select
                        label="Delivery Time"
                        value={selectTime}
                        onChange={handleDeliveryMatrixOptionSelection}
                        options={options}
                      />
                  )
                  : // Show empty dropdown if no options available yet
                  <Select
                    label="Delivery Time"
                    value={selectTime}
                    onChange={handleDeliveryMatrixOptionSelection}
                    options={[]} />}
                {/* Time validation error message */}
                <span style={{ color: "red", display: isTimeSelect, position: "absolute" }}>Please select delivery time</span>
              </div>
            </div>
            
            {/* Save button - triggers validation and opens confirmation modal */}
            <div style={{ position: "absolute", bottom: 20, right: "45%" }}>
              <button
                onClick={handleValidation}
                className={`btn gray-btn d-inline-block mb-3 mr-2`}
                style={{ color: "white" }}>
                Save
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default TransferToOnFleetModal;
